<template>
  <WizardBox title="Purchase Subscription">
    <template v-slot:tools>
      
    </template>
    <template v-slot:content>
      <Spinner v-if="loading" />
      <FormGroup :form="accountForm" v-if="!loading">
        <FormWizard
          ref="accountWizard"
          :form="accountForm"
          :submitting="submitting"
          v-bind:tabList="tabList"
          @stepChange="wizardStepChanged"
          :showFinishButton="false">

          <template v-slot:content>
            
            <FormWizardStep index="1" :active="true">
              <div class="mb-4">
                <ContentTitle>Welcome {{profileCompanyName}}</ContentTitle>
                <p class="mt-2">
                  Some additional information is needed to purchase the subscription. All of the details here can be updated again afterwards.<br />
                </p>
              </div>

              <ContentTitle>Company Details</ContentTitle>
              <FormRow label="Company Name"><FormInputText placeholder="Company Name" v-model="companyDetails.companyName" field="companyName" /></FormRow>
              <FormRow label="Company ACN"><FormInputText placeholder="Company ACN" v-model="companyDetails.companyACN" field="companyACN" /></FormRow>
              <FormRow label="Company ABN"><FormInputText placeholder="Company ABN" v-model="companyDetails.companyABN" field="companyABN" /></FormRow>
              <FormRow label="Email - Reply To Address"><FormInputText placeholder="Reply To" v-model="companyDetails.emailReplyToAddress" field="emailReplyToAddress" /></FormRow>
              <FormRow label="Email - From Name"><FormInputText placeholder="From Name" v-model="companyDetails.emailFromName" field="emailFromName" /></FormRow>

              <FormRow label="Currency Code">
                <FormStaticLabel>{{companyDetails.currencyCode}}</FormStaticLabel>
              </FormRow>
              <FormRow label="Country">
                <FormStaticLabel>{{companyDetails.countryCode}}</FormStaticLabel>
              </FormRow>
              <FormRow label="Timezone Code">
                <FormSearchableSelect :options="timezoneCodeList" placeholder="Timezone" v-model="companyDetails.timezoneCode" field="timezoneCode" />
              </FormRow>

              <FormHorizontalRule />
              <ContentTitle>Account Owner Details</ContentTitle>
              <ContentSubTitle>The details for the account owner user used to login and administer the account</ContentSubTitle>

              <FormRow label="Account Email">
                <FormStaticLabel>{{user.email}}</FormStaticLabel>
              </FormRow>
              <FormRow label="Title">
                <FormInputText placeholder="Title" v-model="user.title" field="user.title" />
              </FormRow>
              <FormRow label="Name">
                <FormInputText placeholder="Name" v-model="user.name" field="user.name" />
              </FormRow>
              <FormRow label="Phone">
                <FormInputText placeholder="Phone" v-model="user.phone" field="user.phone" />
              </FormRow>
              <FormRow label="Mobile">
                <FormInputText placeholder="Mobile" v-model="user.mobile" field="user.mobile" />
              </FormRow>
              <FormRow label="Date Of Birth">
                <FormDate v-model="user.dateOfBirth" field="user.dateOfBirth" />
              </FormRow>
              
            </FormWizardStep>

            <FormWizardStep index="2">
              <ContentTitle>Company Address</ContentTitle>
              <ContentSubTitle>Company address used for setting up your account</ContentSubTitle>

              <FormRow label="Address 1">
                <FormInputText placeholder="Address 1" v-model="companyDetails.locationAddress1" field="locationAddress1" />
              </FormRow>
              <FormRow label="Address 2">
                <FormInputText placeholder="Address 2" v-model="companyDetails.locationAddress2" field="locationAddress2" />
              </FormRow>
              <FormRow label="Suburb">
                <FormInputText placeholder="Suburb" v-model="companyDetails.locationSuburb" field="locationSuburb" />
              </FormRow>
              <FormRow label="State">
                <FormLocationStateSelect placeholder="State" countryCode="AU" v-model="companyDetails.locationState" field="locationState" />
              </FormRow>
              <FormRow label="Postcode">
                <FormInputText placeholder="Postcode" v-model="companyDetails.locationPostcode" field="locationPostcode" />
              </FormRow>
              <FormRow label="Country">
                <FormInputText placeholder="Country" v-model="companyDetails.locationCountry" field="locationCountry" />
              </FormRow>

              <FormHorizontalRule />

              <div class="row">
                <div class="col-2">
                  <ContentTitle>Billing Address</ContentTitle>
                  <ContentSubTitle>Billing address required for subscription checkout</ContentSubTitle>
                </div>
                <div class="col-10">
                  Same as above: <FormCheckbox v-model="companyDetails.billingAddressAsAbove" />
                </div>
              </div>

              <div v-if="!companyDetails.billingAddressAsAbove">
                <FormRow label="Address 1">
                  <FormInputText placeholder="Address 1" v-model="companyDetails.billingAddress1" field="billingAddress1" />
                </FormRow>
                <FormRow label="Address 2">
                  <FormInputText placeholder="Address 2" v-model="companyDetails.billingAddress2" field="billingAddress2" />
                </FormRow>
                <FormRow label="Suburb">
                  <FormInputText placeholder="Suburb" v-model="companyDetails.billingSuburb" field="billingSuburb" />
                </FormRow>
                <FormRow label="State">
                  <FormLocationStateSelect placeholder="State" countryCode="AU" v-model="companyDetails.billingState" field="billingState" />
                </FormRow>
                <FormRow label="Postcode">
                  <FormInputText placeholder="Postcode" v-model="companyDetails.billingPostcode" field="billingPostcode" />
                </FormRow>
                <FormRow label="Country">
                  <FormInputText placeholder="Country" v-model="companyDetails.billingCountry" field="billingCountry" />
                </FormRow>
              </div>
            </FormWizardStep>

            <FormWizardStep index="3">
              <ContentTitle>Choose Subscription Plan</ContentTitle>
              <BillingProductList
                :submittingPurchaseProductId="submittingPurchaseProductId"
                purchaseButtonTitle="Complete and purchase"
                @purchaseClicked="purchaseClicked"
                @contactClicked="contactClicked" />

            </FormWizardStep>
          </template>
        </FormWizard>
      </FormGroup>
    </template>
  </WizardBox>
</template>


<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapState } from "vuex"

import form from '@/mixins/form'
import detectUnload from '@/mixins/detectUnload'
import EventBus from '@/utils/eventBus'

import WizardBox from '@/components/wizardBox'
import WizardLargeOptionButton from '@/components/wizardLargeOptionButton'
import BillingProductList from '@/modules/admin/components/billingProductList'
import ProcessingDialog from '@/components/dialog/processingDialog.vue'

export default {
  name: 'AdminToolSetupBilling',
  mixins: [
    form,
    detectUnload,
  ],
  components: {
    WizardBox,
    WizardLargeOptionButton,
    BillingProductList
  },
  beforeRouteLeave (to, from, next) {
    if(!this.$data.isAccountEdited)
      return next(true)
    
    this.confirmMessage(`Cancel Billing Setup?`, '', {
      html: `You have unsaved changes with the account billing, are you sure you want to leave?<br />You will be able to come back to the billing setup in future`
    })
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  data() {
    return {
      accountForm: {},
      loading: false,
      submitting: false,
      submittingPurchaseProductId: 0,
      isAccountEdited: true,

      timezoneCodeList: [],

      user: {
        title: 'Mr',
        email: '',
        name: '',
        phone: '',
        mobile: '',
        dateOfBirth: null,
      },

      companyDetails: {
        companyName: '',
        companyACN: '',
        companyABN: '',
        currencyCode: 'AUD',
        timezoneCode: '',
        emailReplyToAddress: '',
        emailFromName: '',
        countryCode: 'AU',
        stateCode: 'NSW',
        locationAddress1: '',
        locationAddress2: '',
        locationSuburb: '',
        locationState: 'NSW',
        locationPostcode: '',
        locationCountry: 'Australia',

        billingAddressAsAbove: true,
        billingAddress1: '',
        billingAddress2: '',
        billingSuburb: '',
        billingState: 'NSW',
        billingPostcode: '',
        billingCountry: 'Australia',
      },
    }
  },
  computed: {
    ...mapState({
      isAccountLoaded: state => state.account.isAccountLoaded,
      isAccountInitialBillingSetup: state => state.account.isAccountInitialBillingSetup,
      isAccountActivated: state => state.account.isAccountActivated,
      profileCompanyName: state => state.profile.profileCompanyName,
      profileAccount: state => state.account,
      profileUser: state => state.profile.profileUser
    }),
    tabList() {
      var output = [
        { title: 'Details' },
        { title: 'Address' },
        { title: 'Billing' },
      ]
      return output
    },
  },
  watch: {
    profileCompanyName: function() {
      this.companyDetails.companyName = this.profileCompanyName
    },
    profileUser: {
      deep: true,
      handler: function() {
        this.user.email = this.profileUser.email
        this.user.name = this.profileUser.name
        this.companyDetails.emailReplyToAddress = this.profileUser.email
      }
    }
  },
  mounted() {
    this.companyDetails.companyName = this.profileCompanyName
    this.user.email = this.profileUser.email
    this.user.name = this.profileUser.name
    this.companyDetails.emailReplyToAddress = this.profileUser.email

    this.timezoneCodeList = moment.tz.names().map(tz => { return { name: tz, value: tz } })    
    this.companyDetails.timezoneCode = moment.tz.guess()

    // Get current billing info
    this.requestData()
  },
  methods: {
    wizardStepChanged() {

    },
    requestData() {
      this.loading = true

      // Re-request account data to check if billing setup already
      Promise.all([
        this.$apiService.adminService.getAccount(),
        this.$apiService.adminService.getAccountBilling()
      ])
      .then(results => {
        this.$store.commit("account/setAccountData", results[0].data)

        if(results[0].data.isAccountInitialBillingSetup) {
          // If now setup during load, redirect away
          this.$data.isAccountEdited = false
          this.$router.push('/')
          return
        }

        this.companyDetails.emailFromName = results[0].data.emailFromName

        this.companyDetails.billingAddress1 = results[1].data.account.billingAddress1
        this.companyDetails.billingAddress2 = results[1].data.account.billingAddress2
        this.companyDetails.billingSuburb = results[1].data.account.billingSuburb
        this.companyDetails.billingState = results[1].data.account.billingState
        this.companyDetails.billingPostcode = results[1].data.account.billingPostcode
        this.companyDetails.billingCountry = results[1].data.account.billingCountry

        // Set location if available
        if(results[0].data.accountLocations && results[0].data.accountLocations.length > 0) {
          let location = results[0].data.accountLocations[0]

          this.companyDetails.locationAddress1 = location.address1
          this.companyDetails.locationAddress2 = location.address2
          this.companyDetails.locationSuburb = location.suburb
          this.companyDetails.locationState = location.state
          this.companyDetails.locationPostcode = location.postcode
          this.companyDetails.locationCountry = location.country
        }
      })
      .catch(err => {

      })
      .finally(() => {
        this.loading = false
      })
    },
    purchaseClicked(purchaseInfo) {

      if(this.companyDetails.billingAddressAsAbove) {
        this.companyDetails.billingAddress1 = this.companyDetails.locationAddress1
        this.companyDetails.billingAddress2 = this.companyDetails.locationAddress2
        this.companyDetails.billingSuburb = this.companyDetails.locationSuburb
        this.companyDetails.billingState = this.companyDetails.locationState
        this.companyDetails.billingPostcode = this.companyDetails.locationPostcode
        this.companyDetails.billingCountry = this.companyDetails.locationCountry
      }

      var billingData = {
        companyName: this.companyDetails.companyName,
        companyACN: this.companyDetails.companyACN,
        companyABN: this.companyDetails.companyABN,
        emailReplyToAddress: this.companyDetails.emailReplyToAddress,
        emailFromName: this.companyDetails.emailFromName,
        currencyCode: this.companyDetails.currencyCode,
        timezoneCode: this.companyDetails.timezoneCode,
        countryCode: this.companyDetails.countryCode,
        stateCode: this.companyDetails.stateCode,
        locationAddress1: this.companyDetails.locationAddress1,
        locationAddress2: this.companyDetails.locationAddress2,
        locationSuburb: this.companyDetails.locationSuburb,
        locationState: this.companyDetails.locationState,
        locationPostcode: this.companyDetails.locationPostcode,
        locationCountry: this.companyDetails.locationCountry,
        billingAddress1: this.companyDetails.billingAddress1,
        billingAddress2: this.companyDetails.billingAddress2,
        billingSuburb: this.companyDetails.billingSuburb,
        billingState: this.companyDetails.billingState,
        billingPostcode: this.companyDetails.billingPostcode,
        billingCountry: this.companyDetails.billingCountry,

        'user.title': this.user.title,
        'user.name': this.user.name,
        'user.phone': this.user.phone,
        'user.mobile': this.user.mobile,
        'user.dateOfBirth': this.user.dateOfBirth
      }

      this.submittingPurchaseProductId = purchaseInfo.id

      this.$api.post('/account/billing/initial', billingData)
      .then(response => {
        // Account updated
        EventBus.emitEvent(EventBus.EventTypes.accountUpdated)

        // Prevent guard stopping redirection
        // Manually set account billing setup so we don't keep redirecting back here
        this.$store.commit('account/setAccountInitialBillingSetup')
        this.isAccountEdited = false

        // Redirect to billing to finalise the payment
        this.displayModal(ProcessingDialog, {
          title: 'Setting up purchase',
          message: 'Redirecting to the checkout page',
          onBeforeOpen: (instance) => {
            this.$apiService.adminService.createBillingCheckoutSession({
              stripePriceLineItems: purchaseInfo.priceLineItems,
              isBillingSetup: true
            })
            .then(response => {
                setTimeout(() => {
                  window.location.href = response.data.url
                }, 1000)
            })
            .catch(err => {
              this.submittingPurchaseProductId = 0
              instance.hide()
              this.displayNetworkError(err, 'Failed to connect', 'Error while attempting to connect to billing system')
            })
          }
        })
      })
      .catch(err => {
        this.$data.accountForm = this.processFormResponse(err)
        this.$refs.accountWizard.displayErrors()
      })
      .finally(() => {
        this.submitting = false
        this.submittingPurchaseProductId = 0
      })
    },
    contactClicked(purchaseInfo) {
      this.submittingPurchaseProductId = purchaseInfo.id

      if(this.companyDetails.billingAddressAsAbove) {
        this.companyDetails.billingAddress1 = this.companyDetails.locationAddress1
        this.companyDetails.billingAddress2 = this.companyDetails.locationAddress2
        this.companyDetails.billingSuburb = this.companyDetails.locationSuburb
        this.companyDetails.billingState = this.companyDetails.locationState
        this.companyDetails.billingPostcode = this.companyDetails.locationPostcode
        this.companyDetails.billingCountry = this.companyDetails.locationCountry
      }

      // Save billing info, then redirect to the contact info
      var billingData = {
        companyName: this.companyDetails.companyName,
        companyACN: this.companyDetails.companyACN,
        companyABN: this.companyDetails.companyABN,
        emailReplyToAddress: this.companyDetails.emailReplyToAddress,
        emailFromName: this.companyDetails.emailFromName,
        currencyCode: this.companyDetails.currencyCode,
        timezoneCode: this.companyDetails.timezoneCode,
        countryCode: this.companyDetails.countryCode,
        stateCode: this.companyDetails.locationState,
        locationAddress1: this.companyDetails.locationAddress1,
        locationAddress2: this.companyDetails.locationAddress2,
        locationSuburb: this.companyDetails.locationSuburb,
        locationState: this.companyDetails.locationState,
        locationPostcode: this.companyDetails.locationPostcode,
        locationCountry: this.companyDetails.locationCountry,
        billingAddress1: this.companyDetails.billingAddress1,
        billingAddress2: this.companyDetails.billingAddress2,
        billingSuburb: this.companyDetails.billingSuburb,
        billingState: this.companyDetails.billingState,
        billingPostcode: this.companyDetails.billingPostcode,
        billingCountry: this.companyDetails.billingCountry,

        'user.title': this.user.title,
        'user.name': this.user.name,
        'user.phone': this.user.phone,
        'user.mobile': this.user.mobile,
        'user.dateOfBirth': this.user.dateOfBirth
      }

      this.$api.post('/account/billing/initial', billingData)
      .then(response => {
        window.location.href = `mailto:sales@synsoft.com.au?subject=Purchasing Subscription ${purchaseInfo.name}`

      })
      .catch(err => {
        this.$data.accountForm = this.processFormResponse(err)
        this.$refs.accountWizard.displayErrors()
      })
      .finally(() => {
        this.submitting = false
        this.submittingPurchaseProductId = 0
      })

    }
  }

}
</script>